import { useBaseInsuranceService } from './baseInsuranceService';

/**
 * Service for handling auto insurance policy operations
 * 
 * @returns {Object} Service methods for auto insurance operations
 */
export function useAutoInsuranceService() {
  const { submitForm, fetchData, uploadFile } = useBaseInsuranceService();
  
  /**
   * Submit a policy change request
   * 
   * @param {Object} formData - The form data for the policy change
   * @returns {Promise} Promise resolving to the API response
   */
  const submitPolicyChange = async (formData, isMultipart = false) => {
    console.log('[AutoInsuranceService] Submitting policy change:', isMultipart ? 
      'FormData object (details in network tab)' : formData);
    
    if (isMultipart) {
      // Log FormData entries for debugging
      console.log('[AutoInsuranceService] FormData entries:');
      try {
        // FormData entries can only be iterated once, so we can't log everything
        // but we can try to log some key fields to help with debugging
        if (formData instanceof FormData) {
          console.log('policyNumber:', formData.get('policyNumber'));
          console.log('email:', formData.get('email'));
          console.log('effectiveDate:', formData.get('effectiveDate'));
          console.log('customerType:', formData.get('customerType'));
          // Log which changes are active
          console.log('Changes:', {
            addVehicle: formData.get('addVehicleCheck'),
            deleteVehicle: formData.get('deleteVehicleCheck'),
            replaceVehicle: formData.get('replaceVehicleCheck'),
            addDriver: formData.get('addDriverCheck'),
            deleteDriver: formData.get('deleteDriverCheck'),
            updateAddress: formData.get('updateAddressCheck'),
            updateCoverages: formData.get('updateCoveragesCheck'),
            updateLienholder: formData.get('updateLienholderCheck'),
            other: formData.get('otherCheck')
          });
        }
      } catch (e) {
        console.error('[AutoInsuranceService] Error logging FormData:', e);
      }
    }
    
    return submitForm('/policy-changes/auto', formData, isMultipart);
  };

  /**
   * Get details for an auto policy
   * 
   * @param {string} policyNumber - The policy number
   * @returns {Promise} Promise resolving to the policy details
   */
  const getPolicyDetails = async (policyNumber) => {
    return fetchData(`/auto-policy/${policyNumber}`);
  };

  /**
   * Get available coverages for a policy
   * 
   * @param {string} policyNumber - The policy number
   * @returns {Promise} Promise resolving to the available coverages
   */
  const getAvailableCoverages = async (policyNumber) => {
    return fetchData(`/auto-policy/${policyNumber}/coverages`);
  };

  /**
   * Validate policy number for an auto insurance policy
   * 
   * @param {string} policyNumber - The policy number to validate
   * @returns {Promise} Promise resolving to validation result
   */
  const validatePolicy = async (policyNumber) => {
    return submitForm('/auto-policy/validate', { policyNumber });
  };
  
  /**
   * Upload a document related to auto insurance
   * 
   * @param {File} file - The file to upload
   * @param {Object} metadata - Metadata for the document
   * @returns {Promise} Promise resolving to the upload result
   */
  const uploadDocument = async (file, metadata) => {
    return uploadFile('/auto-policy/upload-document', file, metadata);
  };

  return {
    submitPolicyChange,
    getPolicyDetails,
    getAvailableCoverages,
    validatePolicy,
    uploadDocument
  };
} 